import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FileDownload from 'js-file-download';
import queryString from 'query-string';
import Heading from '@tds/core-heading';
import TDSButton from '@tds/core-button';
import Modal from '@tds/community-modal';
import PropTypes from 'prop-types';
import Filters from '../filters/index';
import Button, { ButtonWrapper } from './css';
import { ContentWrapper, Box } from '../design/css';
import Table from '../design/shared/table';
import request, {
  apiHost,
  USER_RIGHTS,
  config as requestConfig
} from '../utils/request';
import OrderListModel, {
  OrderHeaderModel,
  getOptions,
  RepModel,
  ChannelModel
} from './models';
import OrderDetail from '../orderDetails';
import MessageNotification from '../messageNotification';
import ValidateLoader from '../UI/validateLoader';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { hasLength, getFormattedDate } from '../utils/utils';
import {
  getContentfulData,
  getData,
  contentfulFormConfig
} from '../contentful';
import {
  orderStatus,
  orderDetailsForm,
  wirelineAndSecureBusinessForm,
  orderTypeNew
} from '../utils/constant';

const connectionKey = 'ORDERS';

const initialFilterState = {
  startDate: null,
  endDate: null,
  filterCriteria: null,
  channelId: '',
  assignee: [],
  createdBy: []
};

const Orders = ({ history, location: { search }, locale }) => {
  const localizedStrings = useLocalizedStringsContext();
  const staticLabels = localizedStrings[connectionKey];
  const {
    EXPORT,
    NO_ORDERS,
    LOADING_MESSAGE,
    ORDER_WITH,
    SUCCESS_MESSAGE,
    viewOrderButtonLabel,
    ordersTableTitle,
    proceedWithOrder,
    modifyQuote,
    confirmationDialog,
    confirmationMessage,
    cancel,
    confirm
  } = staticLabels;
  const [loading, setLoading] = useState(false);
  const [filterState, setFilterState] = useState(initialFilterState);
  const config = { ...contentfulFormConfig, locale };
  const {
    startDate,
    endDate,
    assignee,
    channelId,
    createdBy,
    filterCriteria
  } = filterState;
  const filterBody = {
    channelId,
    assignedUserId: assignee.length && assignee[0].id,
    createdBy: createdBy.length && createdBy[0].id,
    startDate: startDate && getFormattedDate(startDate),
    endDate: endDate && getFormattedDate(endDate),
    filterCriteria
  };
  const [data, setData] = useState();
  const [orderDetails, setOrderDetails] = useState();
  const [isOrderUpdated, setOrderUpdated] = useState();
  const [orderSummary, setOrderSummary] = useState(null);
  const [order, setOrder] = useState({});
  const [action, setAction] = useState();
  const [salesRepNames, setSalesRepNames] = useState(null);
  const [assignees, setAssignees] = useState(null);
  const [channels, setChannels] = useState(null);
  const [orderComments, setOrderComments] = useState(null);
  const [tableHeaders, setTableHeaders] = useState(
    new OrderHeaderModel(staticLabels)
  );
  const { page } = queryString.parse(search);
  const profile = request.getProfile();
  const userProfileRole = profile && profile.role.name;
  const userRights = userProfileRole && USER_RIGHTS[userProfileRole];
  const pageSize = 50;
  const pageQuery = `pageNumber=${page || 1}&pageSize=${pageSize}`;
  const statusQuery = `status=${orderStatus.pendingValidation}&status=${orderStatus.validated}`;
  const ordersUrl = `/orders?${pageQuery}&${statusQuery}`;
  const isFilterApplied = hasLength(
    endDate,
    startDate,
    assignee,
    channelId,
    createdBy,
    filterCriteria
  );

  const filterData = {
    salesRepNames,
    assignees,
    channels
  };

  useEffect(() => {
    setTableHeaders(new OrderHeaderModel(staticLabels));
  }, [localizedStrings]);

  const getParams = filters => {
    const params = [];
    Object.keys(filters).map(filter => {
      if (filters[filter]) {
        params.push(`${filter}=${filters[filter]}`);
      }
      return params;
    });
    return params.length ? `&${params.join('&')}` : '';
  };

  const fetchDataFromUrl = url => {
    return request.get(url);
  };
  const fetchOrders = () => {
    return request.get(ordersUrl);
  };

  const fetchData = () => {
    setLoading(true);
    fetchOrders().then(ordersResp => {
      const {
        data: { totalCount: ordersCount, results: orders }
      } = ordersResp;
      setData({
        orders,
        count: ordersCount
      });
      setLoading(false);
    });
  };

  const filterOrders = () => {
    if (page && page > 1) history.push('/orders');
    setLoading(true);
    if (isFilterApplied) {
      request
        .get(`${ordersUrl}${getParams(filterBody)}`)
        .then(({ data: { results: orders, totalCount } }) => {
          setData({ ...data, orders, count: totalCount });
          setLoading(false);
        });
    } else {
      fetchData();
      setLoading(false);
    }
  };

  const updateFilters = (key, value) => {
    setFilterState({ ...filterState, [key]: value });
  };

  const onClickView = id => {
    getContentfulData(config).then(res => {
      const { formCategories } = getData(res);
      const {
        fields: { forms }
      } = formCategories[0];
      if (forms && forms.length >= 3) {
        const {
          fields: { steps }
        } = forms[2];
        if (steps && steps.length) {
          const {
            fields: { formStepFields }
          } = steps[0];
          setOrderSummary(formStepFields);
        }
      }
    });
    setLoading(true);
    Promise.all([
      fetchDataFromUrl(`/orders/${id}`),
      fetchDataFromUrl(`/orders/${id}/comments`)
    ]).then(([orderResp, commentsResp]) => {
      const { data: orders } = orderResp;
      const {
        data: { results: comments }
      } = commentsResp;
      setOrderDetails(orders);
      setOrderComments(comments);
      setOrderUpdated(false);
      setLoading(false);
    });
  };

  const onClickExport = () => {
    axios
      .post(
        `${apiHost}/orders/export/excel?${statusQuery}${getParams(filterBody)}`,
        {},
        { ...requestConfig(), responseType: 'arraybuffer' }
      )
      .then(response => response.data)
      .then(fileData => FileDownload(fileData, 'orders.xlsx'))
      .catch(() => {});
  };

  const onSave = value => {
    setOrderUpdated(value);
  };

  const resetFilters = () => {
    setFilterState({
      ...initialFilterState,
      assignee: [],
      channelOrgCode: [],
      outletCode: [],
      salesRepName: []
    });
  };

  const actions = [
    {
      label: modifyQuote,
      onClick: row => {
        setAction(modifyQuote);
        setOrder({ open: true, orderDetails: row });
      }
    },
    {
      label: proceedWithOrder,
      onClick: row => {
        setAction(proceedWithOrder);
        setOrder({ open: true, orderDetails: row });
      }
    },
    {
      label: viewOrderButtonLabel,
      onClick: row => onClickView(row.id)
    }
  ];

  const getActions = row => {
    return actions.map(({ label, onClick }) => {
      if (row.typeOfInstallation === orderTypeNew && label === modifyQuote)
        return null;
      return (
        <Button key={row.id} onClick={() => onClick(row)}>
          {label}
        </Button>
      );
    });
  };

  const redirect = (row, formName, step) => {
    window.localStorage.setItem('isSovRedirect', 'Yes');
    history.push({
      pathname: `/${row.locale}/${row.categoryName}/${formName}`,
      search: `?orderId=${row.id}&step=${step}&isEditing=true`
    });
  };

  const onConfirm = () => {
    switch (action) {
      case modifyQuote:
        redirect(order.orderDetails, wirelineAndSecureBusinessForm, 2);
        break;
      case proceedWithOrder:
        redirect(order.orderDetails, orderDetailsForm, 1);
        break;
      default:
    }
  };

  useEffect(() => {
    if (isOrderUpdated) {
      setOrderUpdated(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!orderDetails) {
      if (isFilterApplied) filterOrders();
      else fetchData();
    }
  }, [orderDetails, page]);

  useEffect(() => {
    Promise.allSettled([
      fetchDataFromUrl('/salesRepUsers'),
      fetchDataFromUrl('/sovAgentUsers'),
      fetchDataFromUrl('/channels')
    ]).then(
      ([
        // usersResp,
        salesRepNameResp,
        assigneeResponse,
        channelsResp
      ]) => {
        const {
          value: { data: repNames }
        } = salesRepNameResp;
        const {
          value: { data: assigneeData }
        } = assigneeResponse;
        const {
          value: {
            data: { channels: channelList }
          }
        } = channelsResp;
        setSalesRepNames(getOptions(repNames, RepModel));
        setAssignees(getOptions(assigneeData, RepModel));
        setChannels(getOptions(channelList, ChannelModel));
      }
    );
  }, []);
  return (
    <ContentWrapper style={{ backgroundColor: 'white', color: 'black' , color: 'black' ,width : '90%', marginLeft: "auto" ,marginRight :"auto"}}>
      <Box pb={48}>
        <Filters
          updateFilters={(key, value) => updateFilters(key, value)}
          filterState={filterState}
          onApplyFilters={filterOrders}
          onResetFilters={resetFilters}
          data={filterData}
          staticLabels={staticLabels}
        />
      </Box>
      {isOrderUpdated && isOrderUpdated.isUpdated && (
        <Box pt={20} pb={20}>
          <MessageNotification
            variant="success"
            message={`${ORDER_WITH}${isOrderUpdated.orderId}${SUCCESS_MESSAGE}`}
          />
        </Box>
      )}
      {loading ? (
        <ValidateLoader message={LOADING_MESSAGE} />
      ) : (
        <>
          {data && data.orders && data.orders.length ? (
            <>
              <Table
              style={{ backgroundColor: 'white', color: 'black' , color: 'black' ,width : '90%', marginLeft: "auto" ,marginRight :"auto"}}
                title={ordersTableTitle}
                header={tableHeaders}
                data={data.orders}
                actions={getActions}
                dataModel={OrderListModel}
                currentPage={parseInt(page || 1, 10)}
                count={data.count}
                step={pageSize}
                actionsDisplay="grid"
              />
              <ButtonWrapper>
                <TDSButton variant="secondary" onClick={onClickExport}>
                  {EXPORT}
                </TDSButton>
              </ButtonWrapper>
            </>
          ) : (
            <Heading>{NO_ORDERS}</Heading>
          )}
        </>
      )}
      {orderDetails && orderSummary && (
        <OrderDetail
          open={!!orderDetails}
          setOpen={setOrderDetails}
          order={orderDetails}
          labels={orderDetails}
          onSave={onSave}
          staticLabels={staticLabels}
          userRights={userRights}
          assignees={assignees}
          comments={orderComments}
          summary={orderSummary}
        />
      )}
      <Modal
        heading={confirmationDialog}
        bodyText={`${confirmationMessage} ${action} ?`}
        confirmCTAText={confirm}
        cancelCTAText={cancel}
        onConfirm={() => onConfirm()}
        onClose={() => setOrder({ ...order, open: false })}
        isOpen={action && order.open}
      />
    </ContentWrapper>
  );
};

Orders.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  locale: PropTypes.string.isRequired
};

export default Orders;
